import type { MotionInstruction, MotionPlan, Point, ResolvedFigure, SceneShape } from './model.js';

export function compileMotion(from: ResolvedFigure | undefined, to: ResolvedFigure): MotionPlan | undefined {
  if (!from || from.family !== to.family || from.viewport.width !== to.viewport.width || from.viewport.height !== to.viewport.height) return undefined;
  const previous = new Map(from.shapes.map((shape) => [shape.id, center(shape)]));
  const instructions: MotionInstruction[] = to.shapes.map((shape) => {
    const before = previous.get(shape.id);
    const after = center(shape);
    if (!before) return { objectId: shape.id, kind: 'enter', dx: 0, dy: 10 };
    const dx = round(before.x - after.x);
    const dy = round(before.y - after.y);
    return { objectId: shape.id, kind: Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5 ? 'move' : 'stable', dx, dy };
  });
  return {
    id: `${from.geometryFingerprint}:${to.geometryFingerprint}`,
    fromGeometry: from.geometryFingerprint,
    toGeometry: to.geometryFingerprint,
    instructions,
    entering: instructions.filter((item) => item.kind === 'enter').length,
    moving: instructions.filter((item) => item.kind === 'move').length,
    stable: instructions.filter((item) => item.kind === 'stable').length,
  };
}

function center(shape: SceneShape): Point {
  if (shape.kind === 'rect') return { x: shape.box.x + shape.box.width / 2, y: shape.box.y + shape.box.height / 2 };
  if (shape.kind === 'text') return shape.at;
  if (shape.kind === 'line') return { x: (shape.from.x + shape.to.x) / 2, y: (shape.from.y + shape.to.y) / 2 };
  if (shape.kind === 'polygon') {
    const bounds = extrema(shape.points);
    return { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
  }
  const numbers = [...shape.d.matchAll(/-?\d+(?:\.\d+)?/g)].map((match) => Number(match[0]));
  const points: Point[] = [];
  for (let index = 0; index + 1 < numbers.length; index += 2) points.push({ x: numbers[index]!, y: numbers[index + 1]! });
  if (!points.length) return { x: 0, y: 0 };
  const bounds = extrema(points);
  return { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
}

function extrema(points: Point[]): { minX: number; minY: number; maxX: number; maxY: number } {
  return points.reduce((acc, item) => ({
    minX: Math.min(acc.minX, item.x),
    minY: Math.min(acc.minY, item.y),
    maxX: Math.max(acc.maxX, item.x),
    maxY: Math.max(acc.maxY, item.y),
  }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
